import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Download, Heart, Link2, Maximize2, Minimize2, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { defaultOptions, getPattern, getProduct, imageSrc, isPack, productImages, textureSrc } from "@/lib/catalog";
import { useCart } from "@/context/CartContext";
import { isSoldOut } from "@/lib/patternAvailability";
import { activePhrases } from "@/lib/phrases";
import { formatPrice } from "@/lib/format";
import { usePageMeta } from "@/lib/seo";
import { CATALOGO_HEART, CATALOGO_PAGES, CATALOGO_ZONES } from "@/data/catalogoZones";

// Catálogo para folhear, a ecrã inteiro: cada artigo tem um coração ao lado do nome que o guarda na lista, já com o padrão do catálogo.
// A lista fica só neste navegador (ou segue num link, se a cliente a quiser partilhar); comprar é continuar no site, no artigo com o tecido pré-escolhido.
const KEY = "babybib-catalogo-lista-v1";
const RATIO = 595 / 842;
const FLIP_MS = 0.75;
const pageSrc = (n) => `/catalogo/p${String(n).padStart(2, "0")}.jpg`;
const entryKey = (e) => `${e.slug}|${e.patternId ?? ""}`;
const productHref = (e) => `/produto/${e.slug}${e.patternId ? `?tecido=${encodeURIComponent(e.patternId)}` : ""}`;

const clean = (list) => {
  const seen = new Set();
  return (Array.isArray(list) ? list : []).filter((e) => {
    if (!e || typeof e.slug !== "string" || !getProduct(e.slug)) return false;
    const k = entryKey(e);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  }).slice(0, 60).map((e) => ({ slug: e.slug, patternId: e.patternId && getPattern(e.patternId) ? e.patternId : null }));
};
const load = () => {
  try { return clean(JSON.parse(localStorage.getItem(KEY) ?? "[]")); } catch { return []; }
};
const encode = (list) => btoa(list.map(entryKey).join(",")).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
const decode = (code) => {
  try {
    const raw = atob(String(code).slice(0, 4000).replace(/-/g, "+").replace(/_/g, "/"));
    return clean(raw.split(",").map((s) => { const [slug, patternId] = s.split("|"); return { slug, patternId: patternId || null }; }));
  } catch { return []; }
};
const labelOf = (e) => {
  const p = getProduct(e.slug);
  const pt = e.patternId ? getPattern(e.patternId) : null;
  return `${p?.name ?? e.slug}${pt ? ` · ${pt.name}` : ""}`;
};

// Vai direto para o carrinho só quando o padrão do catálogo é tudo o que falta escolher (sem tamanho/material nem partes ou frases por definir);
// nos outros artigos abre-se a página da peça, com o padrão já escolhido, para a cliente acabar de a configurar.
const mustPick = (o) => /tamanho|material/i.test(o.label);
const directEntry = (e) => {
  const product = getProduct(e.slug);
  const pattern = e.patternId ? getPattern(e.patternId) : null;
  if (!product || !pattern || typeof product.price !== "number" || isSoldOut(pattern)) return null;
  if (!(product.patterns ?? []).includes(pattern.id) || isPack(product) || activePhrases(product)) return null;
  if (product.options.some((o) => o.choices.length > 1 && mustPick(o))) return null;
  return { slug: e.slug, patternId: pattern.id, qty: 1, ...(product.options.length ? { options: defaultOptions(product) } : {}) };
};

// Livro de pares: [vazio, 1], [2, 3] ... [30, vazio]. Em ecrãs estreitos mostra-se uma página de cada vez.
const spreadPages = (wide, idx) => {
  if (!wide) return [null, idx + 1];
  const l = idx === 0 ? null : idx * 2;
  const r = idx * 2 + 1;
  return [l, r <= CATALOGO_PAGES ? r : null];
};
const lastIndex = (wide) => (wide ? Math.floor(CATALOGO_PAGES / 2) : CATALOGO_PAGES - 1);
const spreadOfPage = (wide, n) => (wide ? Math.floor(n / 2) : n - 1);

function Sheet({ n, w, h, saved, onToggle, live = true, big = false, hint = false }) {
  if (!n) return <div style={{ width: w, height: h }} />;
  return (
    <div className="relative overflow-hidden bg-linen" style={{ width: w, height: h }} data-testid={`catalogo-page-${n}`}>
      <img src={pageSrc(n)} alt={`Catálogo Babybib, página ${n}`} className="absolute inset-0 h-full w-full select-none object-cover" draggable={false} />
      {live && (CATALOGO_ZONES[n] ?? []).map((z, i) => {
        const e = { slug: z.s, patternId: z.t };
        const on = saved.has(entryKey(e));
        return (
          <button
            key={i}
            type="button"
            onClick={() => onToggle(e)}
            aria-pressed={on}
            aria-label={`${on ? "Tirar da lista" : "Guardar na lista"}: ${labelOf(e)}`}
            data-testid={`catalogo-zone-${n}-${i}`}
            className="group absolute cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-bordeaux"
            style={{ left: `${z.x * 100}%`, top: `${z.y * 100}%`, width: `${z.w * 100}%`, height: `${z.h * 100}%` }}
          >
            <span
              className={`absolute grid aspect-square place-items-center rounded-full shadow-md transition-transform group-hover:scale-110 group-active:scale-95 ${on ? "bg-bordeaux text-white" : "bg-white/95 text-bordeaux"} ${hint && !on ? "animate-pulse ring-2 ring-bordeaux/50" : ""}`}
              style={{ left: `${((z.ax - z.x) / z.w) * 100}%`, top: `${((z.ay - z.y) / z.h) * 100}%`, width: `${(CATALOGO_HEART / z.w) * 100}%`, minWidth: big ? 36 : 28, transform: "translateY(-50%)" }}
            >
              <Heart className={`h-[52%] w-[52%] ${on ? "fill-current" : ""}`} aria-hidden="true" />
            </span>
          </button>
        );
      })}
    </div>
  );
}

function ListItem({ e, onRemove, onAdd, inCart }) {
  const direct = !!directEntry(e);
  const product = getProduct(e.slug);
  const pattern = e.patternId ? getPattern(e.patternId) : null;
  const photo = productImages(product, e.patternId)[0];
  return (
    <li className="flex items-center gap-3 rounded-2xl bg-white p-2.5 shadow-sm" data-testid="catalogo-item">
      <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-linen">
        {photo ? <img src={imageSrc(photo.id, 400)} alt="" className="h-full w-full object-cover" /> : pattern ? <img src={textureSrc(pattern.texture, 240)} alt="" className="h-full w-full object-cover" /> : null}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate font-display text-[15px] leading-tight text-ink">{product.name}</p>
        {pattern && (
          <p className="mt-1 flex items-center gap-1.5 text-xs text-taupe-strong">
            <img src={textureSrc(pattern.texture, 240)} alt="" className="h-3.5 w-3.5 rounded-[3px] object-cover" />
            <span className="truncate">{pattern.name}</span>
          </p>
        )}
        <p className="mt-0.5 text-xs text-taupe">{typeof product.price === "number" ? `desde ${formatPrice(product.price)}` : formatPrice(product.price)}</p>
      </div>
      <div className="flex flex-col items-end gap-1">
        {inCart ? (
          <Link to="/carrinho" className="rounded-full border border-bordeaux/40 px-3.5 py-1.5 text-xs font-medium text-bordeaux" data-testid="catalogo-item-incart">No carrinho</Link>
        ) : direct ? (
          <button type="button" onClick={() => onAdd(e)} className="rounded-full bg-bordeaux px-3.5 py-1.5 text-xs font-medium text-white hover:bg-bordeaux-deep" data-testid="catalogo-item-buy">Adicionar ao carrinho</button>
        ) : (
          <Link to={productHref(e)} className="rounded-full bg-bordeaux px-3.5 py-1.5 text-xs font-medium text-white hover:bg-bordeaux-deep hover:text-white" data-testid="catalogo-item-buy">Escolher e comprar</Link>
        )}
        <button type="button" onClick={() => onRemove(e)} className="grid h-7 w-7 place-items-center rounded-full text-taupe hover:text-bordeaux" aria-label={`Tirar ${labelOf(e)} da lista`}>
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </li>
  );
}

const useSize = (ref) => {
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const on = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    on();
    const ro = new ResizeObserver(on);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return size;
};

const faceCls = "absolute inset-0 [backface-visibility:hidden] [-webkit-backface-visibility:hidden]";

export default function CatalogoPage() {
  usePageMeta("Catálogo", "Folheia o catálogo Babybib e guarda os artigos de que gostas, já com o padrão escolhido.");
  const [params, setParams] = useSearchParams();
  const { add, items } = useCart();
  const nav = useNavigate();
  const [list, setList] = useState(load);
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [anim, setAnim] = useState(null); // { from, to, dir } enquanto a folha vira
  const [full, setFull] = useState(false);
  // Dica da primeira visita: some assim que a cliente guarda um artigo, vira a página ou toca em "Percebi".
  const [hint, setHint] = useState(() => { try { return !localStorage.getItem("babybib-catalogo-dica"); } catch { return false; } });
  const dismissHint = useCallback(() => {
    setHint(false);
    try { localStorage.setItem("babybib-catalogo-dica", "1"); } catch { /* sem armazenamento */ }
  }, []);
  const stageRef = useRef(null);
  const { w: sw, h: sh } = useSize(stageRef);
  const wide = sw >= 760 && sw > sh * 1.05;
  const pad = wide ? 72 : 8;
  const pw = Math.max(0, Math.floor(Math.min(wide ? (sw - pad * 2) / 2 : sw - pad * 2, (sh - 16) * RATIO)));
  const ph = Math.round(pw / RATIO);

  // Lista recebida por link: junta-se à da cliente (sem apagar o que ela já tinha) e o código sai do endereço.
  useEffect(() => {
    const code = params.get("lista");
    if (!code) return;
    const incoming = decode(code);
    if (incoming.length) {
      setList((prev) => clean([...prev, ...incoming]));
      setOpen(true);
      toast.success(`${incoming.length} ${incoming.length === 1 ? "artigo" : "artigos"} na tua lista`);
    }
    setParams({}, { replace: true });
  }, [params, setParams]);

  useEffect(() => {
    try { localStorage.setItem(KEY, JSON.stringify(list)); } catch { /* sem armazenamento: a lista vale só nesta visita */ }
  }, [list]);
  // A página ocupa o ecrã todo: o resto do site não deve andar por baixo.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);
  useEffect(() => {
    const on = () => setFull(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", on);
    return () => document.removeEventListener("fullscreenchange", on);
  }, []);

  // Ao passar de uma página para pares (ou ao contrário), mantém-se a página que se estava a ver.
  const lastWide = useRef(wide);
  useEffect(() => {
    if (lastWide.current === wide) return;
    const cur = lastWide.current ? Math.max(1, idx * 2) : idx + 1;
    lastWide.current = wide;
    setAnim(null);
    setIdx(spreadOfPage(wide, cur));
  }, [wide, idx]);

  const saved = useMemo(() => new Set(list.map(entryKey)), [list]);
  const toggle = useCallback((e) => {
    const k = entryKey(e);
    if (!getProduct(e.slug)) { toast("Este artigo já não está disponível."); return; }
    const entry = { slug: e.slug, patternId: e.patternId && getPattern(e.patternId) ? e.patternId : null };
    dismissHint();
    setList((prev) => (prev.some((x) => entryKey(x) === k) ? prev.filter((x) => entryKey(x) !== k) : [...prev, entry]));
    toast(saved.has(k) ? "Tirado da lista" : `Guardado: ${labelOf(entry)}`, { duration: 1600 });
  }, [saved, dismissHint]);

  const inCart = useCallback((e) => items.some((i) => i.slug === e.slug && i.patternId === e.patternId), [items]);
  const addOne = useCallback((e) => {
    const entry = directEntry(e);
    if (!entry) { nav(productHref(e)); return; }
    add(entry);
    toast.success(`${labelOf(e)} no carrinho`);
  }, [add, nav]);
  const addAll = () => {
    const todo = list.filter((e) => !inCart(e) && directEntry(e));
    todo.forEach((e) => add(directEntry(e)));
    toast.success(todo.length ? `${todo.length} ${todo.length === 1 ? "artigo" : "artigos"} no carrinho` : "Já está tudo no carrinho");
    nav("/carrinho");
  };
  const end = lastIndex(wide);
  const go = useCallback((dir) => {
    if (anim || sw === 0) return;
    const to = idx + dir;
    if (to < 0 || to > end) return;
    dismissHint();
    setAnim({ from: idx, to, dir });
  }, [anim, idx, end, sw, dismissHint]);
  const done = () => {
    setIdx(anim.to);
    setAnim(null);
  };

  useEffect(() => {
    const on = (ev) => {
      if (ev.key === "ArrowRight") go(1);
      if (ev.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, [go]);
  // Deslizar o dedo vira a página.
  const touch = useRef(null);
  const onTouchStart = (ev) => { touch.current = { x: ev.touches[0].clientX, y: ev.touches[0].clientY }; };
  const onTouchEnd = (ev) => {
    const t = touch.current;
    touch.current = null;
    if (!t) return;
    const dx = ev.changedTouches[0].clientX - t.x;
    const dy = ev.changedTouches[0].clientY - t.y;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) go(dx < 0 ? 1 : -1);
  };
  // As páginas seguintes já vêm a caminho.
  useEffect(() => {
    [idx - 1, idx + 1, idx + 2].forEach((i) => spreadPages(wide, Math.min(Math.max(i, 0), end)).forEach((n) => { if (n) new Image().src = pageSrc(n); }));
  }, [idx, wide, end]);

  const toggleFull = () => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else document.documentElement.requestFullscreen?.().catch(() => null);
  };
  const share = async () => {
    const url = `${window.location.origin}/catalogo?lista=${encode(list)}`;
    try {
      if (navigator.share) await navigator.share({ title: "A minha lista Babybib", url });
      else { await navigator.clipboard.writeText(url); toast.success("Link da lista copiado"); }
    } catch { /* partilha cancelada */ }
  };

  // O que se vê em cada metade enquanto a folha vira (a folha tem frente e verso).
  const cur = spreadPages(wide, idx);
  const from = anim ? spreadPages(wide, anim.from) : cur;
  const to = anim ? spreadPages(wide, anim.to) : cur;
  const baseL = anim ? (anim.dir > 0 ? from[0] : to[0]) : cur[0];
  const baseR = anim ? (anim.dir > 0 ? to[1] : from[1]) : cur[1];
  const shown = wide ? cur.filter(Boolean) : [cur[1]];
  const label = shown.length === 2 ? `${shown[0]}-${shown[1]}` : shown[0];
  const sheet = (n, live = false) => <Sheet n={n} w={pw} h={ph} saved={saved} onToggle={toggle} live={live && !anim} big={!wide} hint={hint} />;
  const bookW = wide ? pw * 2 : pw;
  const listButton = (
    <button type="button" onClick={() => setOpen(true)} className={`flex items-center justify-center gap-2 rounded-full bg-bordeaux text-sm font-medium text-white shadow-sm hover:bg-bordeaux-deep ${wide ? "ml-1 h-10 px-4" : "h-12 flex-1 px-5 text-base"}`} data-testid="catalogo-list-open">
      <Heart className="h-4 w-4 fill-current" aria-hidden="true" /> A minha lista
      <span className="grid h-6 min-w-6 place-items-center rounded-full bg-white px-1.5 text-xs font-semibold text-bordeaux" data-testid="catalogo-list-count">{list.length}</span>
    </button>
  );

  return (
    <div className="fixed inset-0 z-[70] flex flex-col bg-[#ece5db]" data-testid="catalogo-page">
      <div className="flex h-12 shrink-0 items-center justify-between gap-2 px-2 sm:px-4">
        <Link to="/" className="inline-flex h-10 items-center gap-1 rounded-full px-2 text-sm text-ink hover:text-bordeaux" aria-label="Voltar ao site"><ChevronLeft className="h-5 w-5" /> <span className="hidden sm:inline">Babybib</span></Link>
        <p className="text-xs tracking-wide text-taupe-strong" aria-live="polite">Página {label} de {CATALOGO_PAGES}</p>
        <div className="flex items-center gap-1">
          <a href="/catalogo/catalogo-babybib.pdf" download className="grid h-10 w-10 place-items-center rounded-full text-ink hover:text-bordeaux" aria-label="Descarregar PDF" data-testid="catalogo-pdf"><Download className="h-5 w-5" /></a>
          {typeof document !== "undefined" && document.fullscreenEnabled && (
            <button type="button" onClick={toggleFull} className="hidden h-10 w-10 place-items-center rounded-full text-ink hover:text-bordeaux sm:grid" aria-label={full ? "Sair do ecrã inteiro" : "Ecrã inteiro"}>{full ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}</button>
          )}
          {wide && listButton}
        </div>
      </div>
      <div className="h-[3px] w-full shrink-0 bg-ink/5" aria-hidden="true"><div className="h-full bg-bordeaux/70 transition-[width] duration-500" style={{ width: `${(Math.max(...shown) / CATALOGO_PAGES) * 100}%` }} /></div>

      <div ref={stageRef} className="relative min-h-0 flex-1" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        {sw > 0 && (
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" style={{ width: bookW, height: ph, perspective: 2600 }}>
            <div className="relative h-full w-full shadow-[0_18px_50px_-12px_rgba(43,34,32,.45)]">
              {wide ? (
                <>
                  <div className="absolute left-0 top-0">{sheet(baseL, true)}</div>
                  <div className="absolute right-0 top-0">{sheet(baseR, true)}</div>
                  <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-1/2 w-6 -translate-x-1/2 bg-gradient-to-r from-transparent via-black/10 to-transparent" />
                </>
              ) : (
                <div className="absolute left-0 top-0">{sheet(anim ? (anim.dir > 0 ? to[1] : from[1]) : cur[1], true)}</div>
              )}

              {anim && (
                <motion.div
                  key={`${anim.from}-${anim.to}`}
                  className="absolute top-0"
                  style={{ width: pw, height: ph, [wide && anim.dir < 0 ? "left" : "right"]: wide && anim.dir < 0 ? 0 : 0, transformStyle: "preserve-3d", transformOrigin: wide && anim.dir < 0 ? "right center" : "left center", zIndex: 5, pointerEvents: "none" }}
                  initial={{ rotateY: !wide && anim.dir < 0 ? -180 : 0 }}
                  animate={{ rotateY: wide ? (anim.dir > 0 ? -180 : 180) : anim.dir > 0 ? -180 : 0 }}
                  transition={{ duration: FLIP_MS, ease: [0.45, 0, 0.25, 1] }}
                  onAnimationComplete={done}
                >
                  {wide ? (
                    <>
                      <div className={faceCls}>{sheet(anim.dir > 0 ? from[1] : from[0])}</div>
                      <div className={faceCls} style={{ transform: "rotateY(180deg)" }}>{sheet(anim.dir > 0 ? to[0] : to[1])}</div>
                    </>
                  ) : (
                    <div className={faceCls}>{sheet(anim.dir > 0 ? from[1] : to[1])}</div>
                  )}
                  <motion.span aria-hidden="true" className="pointer-events-none absolute inset-0 bg-black [backface-visibility:hidden]" initial={{ opacity: 0 }} animate={{ opacity: [0, 0.22, 0] }} transition={{ duration: FLIP_MS }} />
                </motion.div>
              )}
            </div>
          </div>
        )}

        <button type="button" onClick={() => go(-1)} disabled={idx <= 0} className="absolute left-1 top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/85 text-ink shadow disabled:opacity-0 sm:left-4 sm:grid" aria-label="Página anterior" data-testid="catalogo-prev"><ChevronLeft className="h-5 w-5" /></button>
        <button type="button" onClick={() => go(1)} disabled={idx >= end} className="absolute right-1 top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/85 text-ink shadow disabled:opacity-0 sm:right-4 sm:grid" aria-label="Página seguinte" data-testid="catalogo-next"><ChevronRight className="h-5 w-5" /></button>
      </div>

      {!wide && (
        <div className="flex shrink-0 items-center gap-2 px-3 pb-[max(12px,env(safe-area-inset-bottom))] pt-2">
          <button type="button" onClick={() => go(-1)} disabled={idx <= 0} className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-white text-ink shadow disabled:opacity-30" aria-label="Página anterior" data-testid="catalogo-prev-m"><ChevronLeft className="h-6 w-6" /></button>
          {listButton}
          <button type="button" onClick={() => go(1)} disabled={idx >= end} className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-white text-ink shadow disabled:opacity-30" aria-label="Página seguinte" data-testid="catalogo-next-m"><ChevronRight className="h-6 w-6" /></button>
        </div>
      )}

      {hint && sw > 0 && (
        <div className="pointer-events-none absolute inset-x-0 bottom-[84px] z-20 flex justify-center px-4 sm:bottom-8">
          <div className="pointer-events-auto flex max-w-sm items-center gap-3 rounded-2xl bg-ink px-4 py-3 text-ivory shadow-xl" role="note" data-testid="catalogo-hint">
            <p className="text-[13px] leading-snug">{wide ? "Usa as setas para folhear." : "Desliza para folhear."} Toca no <Heart className="inline h-3.5 w-3.5 fill-current align-[-2px]" aria-hidden="true" /> ao lado de cada artigo para o guardares.</p>
            <button type="button" onClick={dismissHint} className="shrink-0 rounded-full bg-ivory px-3 py-1.5 text-xs font-medium text-ink">Percebi</button>
          </div>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-[80] flex items-end justify-end bg-ink/40 sm:items-stretch" onClick={() => setOpen(false)} role="presentation">
          <aside className="flex max-h-[88%] w-full flex-col rounded-t-3xl bg-ivory p-5 pb-[max(20px,env(safe-area-inset-bottom))] shadow-2xl sm:h-full sm:max-h-none sm:max-w-md sm:rounded-none" onClick={(ev) => ev.stopPropagation()} role="dialog" aria-modal="true" aria-label="A minha lista" data-testid="catalogo-list">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-2xl">A minha lista</h2>
              <button type="button" onClick={() => setOpen(false)} className="grid h-10 w-10 place-items-center rounded-full hover:bg-linen" aria-label="Fechar"><X className="h-5 w-5" /></button>
            </div>
            {list.length === 0 ? (
              <p className="mt-10 rounded-3xl bg-linen p-6 text-center text-sm text-taupe-strong">Ainda não guardaste nada. Folheia o catálogo e toca no coração ao lado dos artigos de que gostas.</p>
            ) : (
              <>
                <ul className="mt-5 flex-1 space-y-2.5 overflow-y-auto pr-1">
                  {list.map((e) => <ListItem key={entryKey(e)} e={e} onRemove={toggle} onAdd={addOne} inCart={inCart(e)} />)}
                </ul>
                <button type="button" onClick={addAll} className="bb-btn-primary mt-4 w-full" data-testid="catalogo-add-all">Ir para o carrinho com tudo</button>
                <div className="mt-2 flex gap-2">
                  <button type="button" onClick={share} className="bb-btn-ghost flex-1 gap-2" data-testid="catalogo-share"><Link2 className="h-4 w-4" /> Partilhar lista</button>
                  <button type="button" onClick={() => setList([])} className="bb-btn-ghost" data-testid="catalogo-clear">Limpar</button>
                </div>
                <p className="mt-3 text-xs text-taupe">Os artigos com tamanho, material ou partes por escolher abrem a página da peça, já com o padrão do catálogo.</p>
              </>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}
